import React, { createContext, useContext, useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { SERVICES, saveQuoteRequest, COMPANY } from "../data/mock";

const QuoteContext = createContext({ openQuote: () => {} });
export const useQuote = () => useContext(QuoteContext);

export function QuoteProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ insuranceType: "", name: "", email: "", phone: "", message: "", consent: false });
  const [submitting, setSubmitting] = useState(false);

  const openQuote = (presetType) => {
    if (presetType) setForm((f) => ({ ...f, insuranceType: presetType }));
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.insuranceType) {
      toast.error("Please choose a type of insurance.");
      return;
    }
    if (!form.consent) {
      toast.error("Please agree to the data collection consent.");
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      saveQuoteRequest(form);
      setSubmitting(false);
      setOpen(false);
      setForm({ insuranceType: "", name: "", email: "", phone: "", message: "", consent: false });
      toast.success("Quote request sent!", {
        description: "An advisor will reach out to you shortly. Thank you for choosing Abhi Insurance.",
        className: "quote-success-toast",
      });
    }, 700);
  };

  return (
    <QuoteContext.Provider value={{ openQuote }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl border-0">
          <div className="bg-[#006FB9] px-8 py-6">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-white/10">
                <ShieldCheck className="h-5 w-5 text-[#C5E5F5]" />
              </span>
              <div>
                <h3 className="font-heading text-xl font-bold text-white">Get a Free Quote</h3>
                <p className="text-sm text-[#C5E5F5]">Free consultation · No obligations · {COMPANY.hours.split("·")[0]}</p>
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 px-8 py-6 bg-white">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#102F4D]">Type of Insurance *</label>
              <Select value={form.insuranceType} onValueChange={(v) => set("insuranceType", v)}>
                <SelectTrigger className="h-11 rounded-xl border-[#DCE9F1]" data-testid="quote-insurance-type">
                  <SelectValue placeholder="Choose type of insurance" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input required placeholder="Full Name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-xl border-[#DCE9F1]" data-testid="quote-name" />
              <Input required type="tel" placeholder="Phone Number" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-11 rounded-xl border-[#DCE9F1]" data-testid="quote-phone" />
            </div>
            <Input required type="email" placeholder="E-mail Address" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11 rounded-xl border-[#DCE9F1]" data-testid="quote-email" />
            <Textarea placeholder="Tell us briefly about your needs (optional)" value={form.message} onChange={(e) => set("message", e.target.value)} className="min-h-[80px] rounded-xl border-[#DCE9F1]" data-testid="quote-message" />
            <label className="flex items-start gap-2.5 text-sm text-[#526A7D] cursor-pointer">
              <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" data-testid="quote-consent" />
              <span>I agree that my submitted data is being collected and stored for the purpose of responding to my request.</span>
            </label>
            <button type="submit" disabled={submitting} data-testid="quote-submit" className="btn-primary w-full justify-center">
              {submitting ? "Sending…" : "Send Request"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </QuoteContext.Provider>
  );
}
